import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { create, type StoreApi, useStore } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { CartState, CartActions, QRContext } from '../types/cart'
import type { MenuItem, SelectedModifier, CartItem } from '../types/menu'
import type { FulfillmentType, DeliveryAddress, DeliveryQuote, PromoCode, LoyaltyRedemption, TipSelection, OrderTotals } from '../types/order'
import { generateId, generateCartItemId } from '../utils'
import { useTenantConfig } from './tenant-provider'

type CartStore = CartState & CartActions

function createEmptyTotals(): OrderTotals {
  return {
    subtotalCents: 0,
    taxCents: 0,
    tipCents: 0,
    deliveryFeeCents: 0,
    discountCents: 0,
    loyaltyDiscountCents: 0,
    totalCents: 0,
  }
}

function calculateTotals(
  items: CartItem[],
  taxRate: number,
  deliveryQuote?: DeliveryQuote,
  promoCode?: PromoCode,
  loyaltyRedemption?: LoyaltyRedemption,
  tip?: TipSelection
): OrderTotals {
  const subtotalCents = items.reduce((sum, item) => sum + item.lineTotalCents, 0)

  let discountCents = 0
  if (promoCode) {
    if (promoCode.discountPercent) {
      discountCents = Math.round(subtotalCents * (promoCode.discountPercent / 100))
    } else {
      discountCents = promoCode.discountCents
    }
  }

  const loyaltyDiscountCents = loyaltyRedemption?.discountCents ?? 0
  const afterDiscounts = Math.max(0, subtotalCents - discountCents - loyaltyDiscountCents)
  const taxCents = Math.round(afterDiscounts * taxRate)
  const deliveryFeeCents = deliveryQuote?.feeCents ?? 0
  const tipCents = tip?.amountCents ?? 0

  const totalCents = afterDiscounts + taxCents + deliveryFeeCents + tipCents

  return {
    subtotalCents,
    taxCents,
    tipCents,
    deliveryFeeCents,
    discountCents,
    loyaltyDiscountCents,
    totalCents,
  }
}

function calculateItemLineTotal(item: MenuItem, modifiers: SelectedModifier[], quantity: number): number {
  const basePrice = item.priceCents
  const modifierTotal = modifiers.reduce((sum, m) => sum + m.modifier.priceDeltaCents, 0)
  return (basePrice + modifierTotal) * quantity
}

function createCartStore(tenantId: string, taxRate: number) {
  return create<CartStore>()(
    persist(
      (set) => ({
        id: generateId('cart'),
        tenantId,
        items: [],
        fulfillmentType: 'pickup',
        totals: createEmptyTotals(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        addItem: (item: MenuItem, modifiers: SelectedModifier[], quantity: number, instructions?: string) => {
          const lineTotalCents = calculateItemLineTotal(item, modifiers, quantity)

          const newItem: CartItem = {
            id: generateCartItemId(),
            menuItemId: item.id,
            menuItem: item,
            quantity,
            selectedModifiers: modifiers,
            specialInstructions: instructions,
            lineTotalCents,
          }

          set((state) => {
            const newItems = [...state.items, newItem]
            const newTotals = calculateTotals(
              newItems,
              taxRate,
              state.deliveryQuote,
              state.promoCode,
              state.loyaltyRedemption,
              state.tip
            )
            return {
              items: newItems,
              totals: newTotals,
              updatedAt: new Date().toISOString(),
            }
          })
        },

        updateItemQuantity: (itemId: string, quantity: number) => {
          set((state) => {
            if (quantity <= 0) {
              const newItems = state.items.filter((i) => i.id !== itemId)
              const newTotals = calculateTotals(
                newItems,
                taxRate,
                state.deliveryQuote,
                state.promoCode,
                state.loyaltyRedemption,
                state.tip
              )
              return { items: newItems, totals: newTotals, updatedAt: new Date().toISOString() }
            }

            const newItems = state.items.map((item) => {
              if (item.id !== itemId) return item
              const lineTotalCents = calculateItemLineTotal(item.menuItem, item.selectedModifiers, quantity)
              return { ...item, quantity, lineTotalCents }
            })

            const newTotals = calculateTotals(
              newItems,
              taxRate,
              state.deliveryQuote,
              state.promoCode,
              state.loyaltyRedemption,
              state.tip
            )
            return { items: newItems, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        updateItemModifiers: (itemId: string, modifiers: SelectedModifier[]) => {
          set((state) => {
            const newItems = state.items.map((item) => {
              if (item.id !== itemId) return item
              const lineTotalCents = calculateItemLineTotal(item.menuItem, modifiers, item.quantity)
              return { ...item, selectedModifiers: modifiers, lineTotalCents }
            })

            const newTotals = calculateTotals(
              newItems,
              taxRate,
              state.deliveryQuote,
              state.promoCode,
              state.loyaltyRedemption,
              state.tip
            )
            return { items: newItems, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        updateItemInstructions: (itemId: string, instructions: string) => {
          set((state) => ({
            items: state.items.map((item) =>
              item.id === itemId ? { ...item, specialInstructions: instructions } : item
            ),
            updatedAt: new Date().toISOString(),
          }))
        },

        removeItem: (itemId: string) => {
          set((state) => {
            const newItems = state.items.filter((i) => i.id !== itemId)
            const newTotals = calculateTotals(
              newItems,
              taxRate,
              state.deliveryQuote,
              state.promoCode,
              state.loyaltyRedemption,
              state.tip
            )
            return { items: newItems, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        clearCart: () => {
          set({
            items: [],
            deliveryQuote: undefined,
            promoCode: undefined,
            loyaltyRedemption: undefined,
            tip: undefined,
            totals: createEmptyTotals(),
            updatedAt: new Date().toISOString(),
          })
        },

        setFulfillmentType: (type: FulfillmentType) => {
          set((state) => {
            const deliveryQuote = type === 'delivery' ? state.deliveryQuote : undefined
            const newTotals = calculateTotals(
              state.items,
              taxRate,
              deliveryQuote,
              state.promoCode,
              state.loyaltyRedemption,
              state.tip
            )
            return {
              fulfillmentType: type,
              deliveryQuote,
              totals: newTotals,
              updatedAt: new Date().toISOString(),
            }
          })
        },

        setScheduledFor: (datetime: string | undefined) => {
          set({ scheduledFor: datetime, updatedAt: new Date().toISOString() })
        },

        setDeliveryAddress: (address: DeliveryAddress | undefined) => {
          set({ deliveryAddress: address, updatedAt: new Date().toISOString() })
        },

        setDeliveryQuote: (quote: DeliveryQuote | undefined) => {
          set((state) => {
            const newTotals = calculateTotals(
              state.items,
              taxRate,
              quote,
              state.promoCode,
              state.loyaltyRedemption,
              state.tip
            )
            return { deliveryQuote: quote, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        setTableNumber: (tableNumber: number | undefined) => {
          set((state) => ({
            tableNumber,
            fulfillmentType: tableNumber ? 'table_service' : state.fulfillmentType,
            updatedAt: new Date().toISOString(),
          }))
        },

        applyPromoCode: (code: PromoCode) => {
          set((state) => {
            const newTotals = calculateTotals(
              state.items,
              taxRate,
              state.deliveryQuote,
              code,
              state.loyaltyRedemption,
              state.tip
            )
            return { promoCode: code, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        removePromoCode: () => {
          set((state) => {
            const newTotals = calculateTotals(
              state.items,
              taxRate,
              state.deliveryQuote,
              undefined,
              state.loyaltyRedemption,
              state.tip
            )
            return { promoCode: undefined, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        setLoyaltyRedemption: (redemption: LoyaltyRedemption | undefined) => {
          set((state) => {
            const newTotals = calculateTotals(
              state.items,
              taxRate,
              state.deliveryQuote,
              state.promoCode,
              redemption,
              state.tip
            )
            return { loyaltyRedemption: redemption, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        setTip: (tip: TipSelection | undefined) => {
          set((state) => {
            const newTotals = calculateTotals(
              state.items,
              taxRate,
              state.deliveryQuote,
              state.promoCode,
              state.loyaltyRedemption,
              tip
            )
            return { tip, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },

        setQRContext: (context: QRContext | undefined) => {
          set(() => {
            if (context?.type === 'table' && context.tableNumber) {
              return {
                qrContext: context,
                tableNumber: context.tableNumber,
                fulfillmentType: 'table_service' as FulfillmentType,
                updatedAt: new Date().toISOString(),
              }
            }
            return { qrContext: context, updatedAt: new Date().toISOString() }
          })
        },

        recalculateTotals: () => {
          set((state) => {
            const newItems = state.items.map((item) => ({
              ...item,
              lineTotalCents: calculateItemLineTotal(item.menuItem, item.selectedModifiers, item.quantity),
            }))
            const newTotals = calculateTotals(
              newItems,
              taxRate,
              state.deliveryQuote,
              state.promoCode,
              state.loyaltyRedemption,
              state.tip
            )
            return { items: newItems, totals: newTotals, updatedAt: new Date().toISOString() }
          })
        },
      }),
      {
        name: `orderflow-cart-${tenantId}`,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          id: state.id,
          tenantId: state.tenantId,
          items: state.items,
          fulfillmentType: state.fulfillmentType,
          scheduledFor: state.scheduledFor,
          deliveryAddress: state.deliveryAddress,
          tableNumber: state.tableNumber,
          qrContext: state.qrContext,
          createdAt: state.createdAt,
        }),
      }
    )
  )
}

const CartContext = createContext<StoreApi<CartStore> | null>(null)

interface CartProviderProps {
  children: ReactNode
}

export function CartProvider({ children }: CartProviderProps) {
  const tenant = useTenantConfig()
  const storeRef = { current: createCartStore(tenant.id, tenant.operations.taxRate) }

  useEffect(() => {
    storeRef.current.getState().recalculateTotals()
  }, [])

  return <CartContext.Provider value={storeRef.current}>{children}</CartContext.Provider>
}

export function useCartStore<T>(selector: (state: CartStore) => T): T {
  const store = useContext(CartContext)
  if (!store) {
    throw new Error('useCartStore must be used within a CartProvider')
  }
  return useStore(store, selector)
}

export function useCart() {
  return {
    items: useCartStore((s) => s.items),
    totals: useCartStore((s) => s.totals),
    fulfillmentType: useCartStore((s) => s.fulfillmentType),
    scheduledFor: useCartStore((s) => s.scheduledFor),
    deliveryAddress: useCartStore((s) => s.deliveryAddress),
    deliveryQuote: useCartStore((s) => s.deliveryQuote),
    tableNumber: useCartStore((s) => s.tableNumber),
    promoCode: useCartStore((s) => s.promoCode),
    loyaltyRedemption: useCartStore((s) => s.loyaltyRedemption),
    tip: useCartStore((s) => s.tip),
    qrContext: useCartStore((s) => s.qrContext),
    itemCount: useCartStore((s) => s.items.reduce((sum, item) => sum + item.quantity, 0)),
  }
}

export function useCartActions() {
  return {
    addItem: useCartStore((s) => s.addItem),
    updateItemQuantity: useCartStore((s) => s.updateItemQuantity),
    updateItemModifiers: useCartStore((s) => s.updateItemModifiers),
    updateItemInstructions: useCartStore((s) => s.updateItemInstructions),
    removeItem: useCartStore((s) => s.removeItem),
    clearCart: useCartStore((s) => s.clearCart),
    setFulfillmentType: useCartStore((s) => s.setFulfillmentType),
    setScheduledFor: useCartStore((s) => s.setScheduledFor),
    setDeliveryAddress: useCartStore((s) => s.setDeliveryAddress),
    setDeliveryQuote: useCartStore((s) => s.setDeliveryQuote),
    setTableNumber: useCartStore((s) => s.setTableNumber),
    applyPromoCode: useCartStore((s) => s.applyPromoCode),
    removePromoCode: useCartStore((s) => s.removePromoCode),
    setLoyaltyRedemption: useCartStore((s) => s.setLoyaltyRedemption),
    setTip: useCartStore((s) => s.setTip),
    setQRContext: useCartStore((s) => s.setQRContext),
  }
}
